import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useFetcher } from '@remix-run/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  AdvancedOrderPanel,
  DeliverySchedule,
} from '../products/widget/AdvancedOrderPanel';
import { CurrencyCode } from '~/generated/graphql';
import { CartActionData } from '~/routes/api.active-order';
import {
  deriveScheduledOccurrenceCount,
  isScheduledDateOnOrAfterMinimum,
} from '~/utils/scheduled-order';

interface EditScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencyCode: CurrencyCode;
  line: any; // OrderLine fragment
}

export default function EditScheduleModal({
  isOpen,
  onClose,
  currencyCode,
  line,
}: EditScheduleModalProps) {
  const fetcher = useFetcher<CartActionData>();
  const [schedule, setSchedule] = useState<DeliverySchedule | null>(null);
  const [initialData, setInitialData] = useState<DeliverySchedule | null>(null);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'custom'>(
    'daily',
  );
  const [quantityPerDelivery, setQuantityPerDelivery] = useState<number | null>(
    null,
  );
  const [quantityNeedsCorrection, setQuantityNeedsCorrection] = useState(false);
  const [legacyOccurrenceCount, setLegacyOccurrenceCount] = useState<
    number | null
  >(null);
  const scheduleDateIsValid = schedule
    ? isScheduledDateOnOrAfterMinimum(
        schedule.isRepeatOrder
          ? schedule.startDate
          : schedule.singleDeliveryDate,
      )
    : false;

  // Initialize schedule from line customFields
  useEffect(() => {
    if (line?.customFields) {
      // @ts-ignore
      const {
        frequency: freq,
        startDate,
        endDate,
        intervalStep,
        itemType,
        daysOfWeek,
        quantityPerDelivery: storedQuantityPerDelivery,
      } = line.customFields || {};

      const isRepeat = itemType === 'recurring';
      const initialFreq = freq || 'daily';
      const occurrenceCount = deriveScheduledOccurrenceCount({
        itemType,
        frequency: initialFreq,
        intervalStep,
        startDate,
        endDate,
        daysOfWeek,
      });
      const aggregateQuantity = Number(line.quantity);
      const explicitQuantity = Number(storedQuantityPerDelivery);
      const hasExplicitQuantity =
        Number.isInteger(explicitQuantity) && explicitQuantity > 0;

      setLegacyOccurrenceCount(occurrenceCount);
      if (hasExplicitQuantity) {
        setQuantityPerDelivery(explicitQuantity);
        setQuantityNeedsCorrection(false);
      } else if (
        occurrenceCount &&
        Number.isInteger(aggregateQuantity) &&
        aggregateQuantity > 0 &&
        aggregateQuantity % occurrenceCount === 0
      ) {
        setQuantityPerDelivery(aggregateQuantity / occurrenceCount);
        setQuantityNeedsCorrection(false);
      } else {
        // Never reuse an ambiguous aggregate legacy quantity for every
        // delivery. The customer must explicitly correct it below.
        setQuantityPerDelivery(null);
        setQuantityNeedsCorrection(true);
      }

      // Derive selected day from start date if available
      let selectedDays: string[] = [];
      if (daysOfWeek && Array.isArray(daysOfWeek) && daysOfWeek.length > 0) {
        selectedDays = daysOfWeek;
      } else if (startDate) {
        const date = new Date(startDate);
        const daysMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        selectedDays = [daysMap[date.getDay()]];
      }

      setFrequency(initialFreq);

      const initial: DeliverySchedule = {
        frequency: initialFreq,
        startDate: startDate || new Date().toISOString().split('T')[0],
        endDate: endDate || new Date().toISOString().split('T')[0],
        selectedDays: selectedDays,
        customInterval: intervalStep || 0,
        orderCount: occurrenceCount || 0,
        isRepeatOrder: isRepeat,
        singleDeliveryDate: !isRepeat
          ? startDate || new Date().toISOString().split('T')[0]
          : undefined,
      };
      setSchedule(initial);
      setInitialData(initial);
    }
  }, [line]);

  const handleUpdate = () => {
    if (!schedule || !scheduleDateIsValid) return;

    // Calculate quantity based on subscriptions
    // @ts-ignore
    const newQuantity = Number(quantityPerDelivery);
    if (!Number.isInteger(newQuantity) || newQuantity <= 0) return;

    // Construct custom fields
    const fields = {
      itemType: schedule.isRepeatOrder ? 'recurring' : 'future',
      frequency: schedule.frequency,
      intervalStep: schedule.customInterval,
      startDate: schedule.isRepeatOrder
        ? new Date(schedule.startDate).toISOString()
        : new Date(schedule.singleDeliveryDate || '').toISOString(),
      endDate: schedule.isRepeatOrder
        ? new Date(schedule.endDate).toISOString()
        : new Date(schedule.singleDeliveryDate || '').toISOString(), // End = Start for future
      totalDeliveries: schedule.orderCount,
      quantityPerDelivery: newQuantity,
      daysOfWeek: schedule.selectedDays,
    };

    fetcher.submit(
      {
        action: 'updateLineSchedule',
        lineId: line.id,
        variantId: line.productVariant.id,
        quantity: newQuantity.toString(),
        customFields: JSON.stringify(fields),
      },
      { method: 'post', action: '/api/active-order' },
    );
  };

  const handleOrderCountChange = useCallback((count: number) => {
    setSchedule((prev) => (prev ? { ...prev, orderCount: count } : null));
  }, []);

  useEffect(() => {
    // Check for success in Fetcher data (typed)
    if (
      fetcher.state === 'idle' &&
      fetcher.data &&
      fetcher.data.successMessage
    ) {
      onClose(); // Close on success
    }
  }, [fetcher.state, fetcher.data]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block transform rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div className="flex items-center justify-between mb-4">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Update Plan
                </Dialog.Title>
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-2">
                {schedule && initialData && (
                  <AdvancedOrderPanel
                    frequency={frequency}
                    onFrequencyChange={setFrequency}
                    onOrderCountChange={handleOrderCountChange}
                    onScheduleChange={setSchedule}
                    initialSchedule={initialData}
                  />
                )}
              </div>

              {quantityNeedsCorrection ? (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <label
                    htmlFor={`quantity-per-delivery-${line.id}`}
                    className="block text-sm font-medium text-amber-900"
                  >
                    Quantity per delivery
                  </label>
                  <p className="mt-1 text-xs text-amber-800">
                    This legacy plan stores an aggregate quantity of{' '}
                    {line.quantity}
                    {legacyOccurrenceCount
                      ? ` across ${legacyOccurrenceCount} scheduled deliveries, but it cannot be divided evenly.`
                      : ', and its delivery count cannot be derived safely.'}{' '}
                    Enter the intended quantity for each delivery before
                    updating the plan.
                  </p>
                  <input
                    id={`quantity-per-delivery-${line.id}`}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={quantityPerDelivery ?? ''}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      setQuantityPerDelivery(
                        Number.isInteger(value) && value > 0 ? value : null,
                      );
                    }}
                    className="mt-3 block w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#E80245] focus:outline-none focus:ring-1 focus:ring-[#E80245]"
                  />
                </div>
              ) : legacyOccurrenceCount &&
                !line.customFields?.quantityPerDelivery ? (
                <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                  Legacy quantity {line.quantity} will be safely converted to{' '}
                  <strong>{quantityPerDelivery} per delivery</strong> across{' '}
                  {legacyOccurrenceCount} deliveries.
                </div>
              ) : null}

              {fetcher.data?.error && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                  {fetcher.data.error.message}
                </div>
              )}

              <div className="mt-5 sm:mt-6 flex gap-3 flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-[#E80245] px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-[#D0023D] focus:outline-none sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpdate}
                  disabled={
                    fetcher.state !== 'idle' ||
                    !schedule ||
                    schedule.orderCount === 0 ||
                    !scheduleDateIsValid ||
                    !quantityPerDelivery
                  }
                >
                  {fetcher.state !== 'idle' ? 'Updating...' : 'Update Cart'}
                </button>
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
